import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TroubleshootingDetails from '../../components/tickets/TroubleshootingDetails';
import AccountManagementDetails from '../../components/tickets/AccountManagementDetails';
import DocumentUploadDetails from '../../components/tickets/DocumentUploadDetails';
import TechnicalAssistanceDetails from '../../components/tickets/TechnicalAssistanceDetails';


const API_BASE_URL = import.meta.env.VITE_API_URL;

// Custom SVG Icons
const ScheduleIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
  </svg>
);

const PersonIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
);

const CategoryIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const PriorityIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const CloseIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [comment, setComment] = useState('');
  const [ictDetailsDialogOpen, setIctDetailsDialogOpen] = useState(false);
  const [ictDetails, setIctDetails] = useState({
    assignedTo: '',
    diagnosisDetails: '',
    fixDetails: '',
    dateFixed: '',
    recommendations: ''
  });
  const pageSize = 10;

  // Add notification state
  const [showNotification, setShowNotification] = useState(false);
  const [currentNotification, setCurrentNotification] = useState(null);

  // Memoized helper functions defined first to avoid use-before-define issues
  const getStatusColor = useCallback((status) => {
    const colors = {
      PENDING: 'bg-orange-50 text-orange-600 border-orange-200',
      IN_PROGRESS: 'bg-blue-50 text-blue-600 border-blue-200',
      RESOLVED: 'bg-green-50 text-green-600 border-green-200',
      CLOSED: 'bg-gray-50 text-gray-600 border-gray-200',
    };
    return colors[status] || 'bg-gray-50 text-gray-600 border-gray-200';
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) {
      // For new dateFixed fields, automatically set to current date and time
      const now = new Date();
      // Format: YYYY-MM-DDThh:mm
      return now.toISOString().slice(0, 16);
    }
    
    // Handle existing dates with timezone adjustments
    const date = new Date(dateString);
    
    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // Format to hh:mm with local timezone
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    // Combine as YYYY-MM-DDThh:mm
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);

  // Add notification handling functions
  const addNotification = useCallback((message, type = 'info', duration = 6000) => {
    const newNotification = {
      id: Date.now(),
      message,
      type,
      duration,
    };
    setCurrentNotification(newNotification);
    setShowNotification(true);
  }, []);

  const handleNotificationClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowNotification(false);
  }, []);

  // Add this useEffect to reset state when ticket status changes
  useEffect(() => {
    if (ticket) {
      setNewStatus(ticket.status);
      
      // Reset ICT details when ticket status changes
      setIctDetails({
        assignedTo: ticket.ictAssignedTo || '',
        diagnosisDetails: ticket.ictDiagnosisDetails || '',
        fixDetails: ticket.ictFixDetails || '',
        dateFixed: ticket.ictDateFixed ? formatDateForInput(ticket.ictDateFixed) : '',
        recommendations: ticket.ictRecommendations || ''
      });
      
      // Reset comment for status updates
      setComment('');
    }
  }, [ticket, formatDateForInput]);

  // Fetch ticket details
  const fetchTicketDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/tickets/${id}?pageSize=${pageSize}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const ticketData = response.data.data;
        
        // Format dates for display
        const formattedTicket = {
          ...ticketData,
          createdAt: ticketData.createdAt ? new Date(ticketData.createdAt) : null,
          updatedAt: ticketData.updatedAt ? new Date(ticketData.updatedAt) : null,
          dateOfRequest: ticketData.dateOfRequest ? new Date(ticketData.dateOfRequest) : null,
          ictDateFixed: ticketData.ictDateFixed ? new Date(ticketData.ictDateFixed) : null,
          // Ensure category specific details are properly formatted
          categorySpecificDetails: ticketData.categorySpecificDetails || {
            type: ticketData.category,
            details: {}
          }
        };

        setTicket(formattedTicket);
        setNewStatus(formattedTicket.status);
        
        // If ticket has ICT details, update the ICT details state
        if (formattedTicket.ictAssignedTo) {
          setIctDetails({
            assignedTo: formattedTicket.ictAssignedTo,
            diagnosisDetails: formattedTicket.ictDiagnosisDetails || '',
            fixDetails: formattedTicket.ictFixDetails || '',
            dateFixed: formattedTicket.ictDateFixed ? formatDateForInput(formattedTicket.ictDateFixed) : '',
            recommendations: formattedTicket.ictRecommendations || ''
          });
        }
      } else {
        throw new Error(response.data.message || 'Failed to load ticket details');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load ticket details';
      addNotification(errorMessage, 'error');
      
      // If the error is due to authentication, redirect to login
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, pageSize, formatDateForInput, addNotification]);

  useEffect(() => {
    fetchTicketDetails();
  }, [fetchTicketDetails]);

  // Handle status update
  const handleStatusUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/admin/tickets/${id}/status`,
        { 
          status: newStatus,
          comment: comment 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setStatusDialogOpen(false);
        fetchTicketDetails();
        addNotification(`Ticket status updated to ${newStatus.replace(/_/g, ' ')}`, 'success');

        // Send email notification when moving from RESOLVED to IN_PROGRESS
        if (ticket.status === 'RESOLVED' && newStatus === 'IN_PROGRESS') {
          try {
            await axios.post(
              `${API_BASE_URL}/admin/tickets/${id}/send-edit-notification`,
              {
                email: ticket.email,
                name: ticket.name,
                trackingId: ticket.trackingId,
                category: ticket.category,
                subject: ticket.subject || ticket.documentSubject
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            addNotification('Edit notification email sent successfully', 'success');
          } catch (emailError) {
            console.error('Error sending edit notification email:', emailError);
            addNotification('Failed to send edit notification email', 'warning');
          }
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update ticket status';
      addNotification(errorMessage, 'error');
    }
  };

  // Handle document download
  const handleDownload = async (fileUrl) => {
    try {
      if (!fileUrl) {
        addNotification('No file available for download', 'error');
        return;
      }

      // If it's a Google Drive URL, open it in a new tab
      if (fileUrl.includes('drive.google.com')) {
        window.open(fileUrl, '_blank');
        return;
      }

      // For local files
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/documents/${fileUrl}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileUrl.split('/').pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      addNotification('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Download error:', error);
      addNotification('Failed to download file', 'error');
    }
  };

  // Update the onClick handler for the Take Action button
  const handleTakeActionClick = () => {
    setIctDetails({
      assignedTo: ticket.ictAssignedTo || '',
      diagnosisDetails: ticket.ictDiagnosisDetails || '',
      fixDetails: ticket.ictFixDetails || '',
      dateFixed: ticket.ictDateFixed ? formatDateForInput(ticket.ictDateFixed) : '',
      recommendations: ticket.ictRecommendations || ''
    });
    setNewStatus('IN_PROGRESS');
    setIctDetailsDialogOpen(true);
  };

  // Update the onClick handler for the Resolve Ticket button
  const handleResolveClick = () => {
    // Auto-fill diagnosis, fix details, and recommendations based on category
    let diagnosisDetails = '';
    let fixDetails = '';
    let recommendations = '';

    // Get current date and time for the date fixed field
    const now = new Date();
    const formattedDateTime = formatDateForInput(now);

    switch (ticket.category) {
      case 'TROUBLESHOOTING':
        diagnosisDetails = `Issue: ${ticket.categorySpecificDetails?.details?.issue || 'Not specified'}\n` +
                         `Description: ${ticket.categorySpecificDetails?.details?.description || 'Not specified'}\n` +
                         `Location: ${ticket.location?.type === 'SCHOOL' ? 'School' : 'SDO'} - ${ticket.location?.type === 'SCHOOL' ? ticket.location.name : 'Imus City'}` +
                         `${ticket.location?.type === 'SCHOOL' ? '\nSchool Level: ' + (ticket.location.level || 'Not specified') : '\nDepartment: Information and Communications Technology Unit'}`;
        fixDetails = 'Issue has been resolved through troubleshooting and necessary repairs.';
        recommendations = '1. Regular maintenance is recommended to prevent similar issues.\n' +
                         '2. Keep the system updated with the latest software patches.\n' +
                         '3. Report any recurring issues immediately for further investigation.';
        break;

      case 'ACCOUNT_MANAGEMENT':
        diagnosisDetails = `Account Type: ${ticket.accountType || 'Not specified'}\n` +
                         `Action Type: ${ticket.actionType || 'Not specified'}\n` +
                         `Requested by: ${ticket.name || 'Not specified'}\n` +
                         `Email: ${ticket.email || 'Not specified'}`;
        fixDetails = ticket.actionType === 'CREATE' 
          ? 'New account has been created and credentials have been provided.'
          : 'Account password has been successfully reset and new credentials have been provided.';
        recommendations = '1. Keep your credentials secure and do not share them with others.\n' +
                         '2. Enable two-factor authentication for added security.\n' +
                         '3. Change your password regularly and use a strong password combination.';
        break;

      case 'DOCUMENT_UPLOAD':
        diagnosisDetails = `Document Type: ${ticket.categorySpecificDetails?.details?.documentType || 'Not specified'}\n` +
                         `Subject: ${ticket.documentSubject || 'Not specified'}\n` +
                         `Requested by: ${ticket.name || 'Not specified'}\n` +
                         `Department/School: ${ticket.department || ticket.schoolName || 'Not specified'}`;
        fixDetails = 'Document has been processed and uploaded to the system.';
        recommendations = '1. Keep a backup copy of all important documents.\n' +
                         '2. Ensure documents are properly labeled and organized.\n' +
                         '3. Verify document integrity after upload.';
        break;

      case 'TECHNICAL_ASSISTANCE':
        diagnosisDetails = `Assistance Type: ${ticket.categorySpecificDetails?.details?.assistanceType || 'Not specified'}\n` +
                         `Description: ${ticket.categorySpecificDetails?.details?.description || 'Not specified'}\n` +
                         `Location: ${ticket.location?.type === 'SCHOOL' ? 'School' : 'SDO'} - ${ticket.location?.type === 'SCHOOL' ? ticket.location.name : 'Imus City'}` +
                         `${ticket.location?.type === 'SCHOOL' ? '\nSchool Level: ' + (ticket.location.level || 'Not specified') : '\nDepartment: Information and Communications Technology Unit'}`;
        fixDetails = 'Technical assistance has been provided and the issue has been resolved.';
        recommendations = '1. Follow the provided instructions carefully.\n' +
                         '2. Contact ICT support if you encounter any difficulties.\n' +
                         '3. Keep your system updated and maintain regular backups.';
        break;

      default:
        diagnosisDetails = 'Ticket resolved';
        fixDetails = 'Issue has been resolved.';
        recommendations = '1. Please contact ICT support if you need further assistance.\n' +
                         '2. Keep your system updated and maintain regular backups.\n' +
                         '3. Report any issues promptly for timely resolution.';
    }

    setIctDetails({
      assignedTo: ticket.ictAssignedTo || '',
      diagnosisDetails: diagnosisDetails,
      fixDetails: fixDetails,
      dateFixed: formattedDateTime,
      recommendations: recommendations
    });
    setNewStatus('RESOLVED');
    setIctDetailsDialogOpen(true);
  };

  const handleIctActionClick = async () => {
    try {
      // Input validation
      if (newStatus === 'RESOLVED') {
        if (!ictDetails.dateFixed) {
          addNotification('Please provide the date fixed', 'error');
          return;
        }
        if (!ictDetails.fixDetails) {
          addNotification('Please provide fix details', 'error');
          return;
        }
      } else if (!ictDetails.assignedTo) {
        addNotification('Please provide the name of the ICT staff assigned', 'error');
        return;
      }

      // Set loading or disable buttons during API calls
      setLoading(true);
      
      try {
        // Prepare ICT details - ensure dateFixed is properly formatted for backend
        const formattedDetails = {
          ...ictDetails,
          // Make sure dateFixed is properly formatted if it exists
          dateFixed: ictDetails.dateFixed ? new Date(ictDetails.dateFixed).toISOString() : null
        };
        
        // 1. First update ICT details
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_BASE_URL}/admin/tickets/${id}/ict-details`,
          formattedDetails,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        // 2. Then update the status with skipEmail flag
        const statusResponse = await axios.put(
          `${API_BASE_URL}/admin/tickets/${id}/status`,
          { 
            status: newStatus,
            comment: comment,
            skipEmail: true // Skip automatic email in the backend
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // 3. Send appropriate email based on status change
        if (statusResponse.data.success) {
          try {
        if (newStatus === 'IN_PROGRESS') {
          await axios.post(
            `${API_BASE_URL}/admin/tickets/${id}/send-in-progress`,
            {
              email: ticket.email,
              name: ticket.name,
              trackingId: ticket.trackingId,
              category: ticket.category,
              subject: ticket.subject || ticket.documentSubject,
              ictAssignedTo: ictDetails.assignedTo,
              accountType: ticket.accountType,
              actionType: ticket.actionType
            },
                { headers: { Authorization: `Bearer ${token}` } }
          );
          addNotification('In-progress notification email sent successfully', 'success');
        } else if (newStatus === 'RESOLVED') {
          await axios.post(
            `${API_BASE_URL}/admin/tickets/${id}/send-resolution`,
            {
              email: ticket.email,
              name: ticket.name,
              trackingId: ticket.trackingId,
              category: ticket.category,
              subject: ticket.subject || ticket.documentSubject,
              ictFixDetails: ictDetails.fixDetails || 'No fix details provided',
              ictRecommendations: ictDetails.recommendations,
              accountType: ticket.accountType,
              actionType: ticket.actionType
            },
                { headers: { Authorization: `Bearer ${token}` } }
          );
          addNotification('Resolution email sent successfully', 'success');
        }
        } catch (emailError) {
        console.error('Error sending status email:', emailError);
        addNotification(`Ticket updated but failed to send ${newStatus.toLowerCase()} email`, 'warning');
      }
      
          // Show success message and update UI
          addNotification(`Ticket status updated to ${newStatus.replace(/_/g, ' ')}`, 'success');
        }
        
        // Close dialog and refresh data
      setIctDetailsDialogOpen(false);
        fetchTicketDetails(); // This will trigger useEffect to reset the button states
      } catch (apiError) {
        console.error('API error:', apiError);
        const errorMessage = apiError.response?.data?.message || apiError.message || 'Failed to update ticket';
      addNotification(errorMessage, 'error');
      }
    } catch (err) {
      console.error('General error:', err);
      addNotification('An error occurred while processing your request', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] p-4 sm:p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="font-poppins mt-4 text-gray-600 text-sm sm:text-base">
          Loading ticket details...
        </p>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg font-poppins">
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Ticket not found
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-gray-50 min-h-screen">
      <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg">
        {/* Header */}
        <div className="mb-4 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold font-poppins bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
            Tracking ID: {ticket.trackingId}
          </h1>
          <div className="flex w-full sm:w-auto gap-3">
            <button
              onClick={() => navigate('/admin/tickets')}
              className="flex-1 sm:flex-none px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-poppins"
            >
              Back to List
            </button>
            {ticket.status === 'RESOLVED' && (
              <button
                onClick={() => {
                  setNewStatus('IN_PROGRESS');
                  setComment('Ticket requires additional attention or changes.');
                  setStatusDialogOpen(true);
                }}
                className="flex-1 sm:flex-none px-4 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg hover:from-red-700 hover:to-red-600 transition-colors font-poppins shadow-lg"
              >
                Edit Status
              </button>
            )}
          </div>
        </div>

        <div className="border-b border-gray-200 mb-4 sm:mb-8"></div>

        {/* Ticket Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {/* Basic Information */}
          <div className="bg-white p-4 sm:p-6 h-full rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-blue-600 mb-4 font-poppins">
              Basic Information
            </h2>
            <div className="space-y-4">
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-1">Tracking ID</div>
                <div className="text-gray-900 font-mono">{ticket.trackingId}</div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Status</div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                  {ticket.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Category</div>
                <div className="flex items-center gap-2">
                  <CategoryIcon className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-900">{ticket.category.replace(/_/g, ' ')}</span>
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Priority</div>
                <div className="flex items-center gap-2">
                  <PriorityIcon className={`w-5 h-5 ${ticket.priority === 'HIGH' ? 'text-red-600' : 'text-blue-600'}`} />
                  <span className={`${ticket.priority === 'HIGH' ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                    {ticket.priority}
                  </span>
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Location & Department</div>
                <div className="text-sm text-gray-900">
                  {ticket.location ? (
                    <>
                      <div>Location: {ticket.location.type === 'SCHOOL' ? 'School' : 'SDO'} - {ticket.location.type === 'SDO' ? 'Imus City' : ticket.location.name}</div>
                      {ticket.location.type === 'SCHOOL' && (
                        <div>School Level: {ticket.location.level || 'Not specified'}</div>
                      )}
                      {ticket.location.type === 'SDO' && (
                        <div>Department: Information and Communications Technology Unit</div>
                      )}
                    </>
                  ) : (
                    <div>Location not specified</div>
                  )}
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Created At</div>
                <div className="flex items-center gap-2">
                  <ScheduleIcon className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-900">{formatDate(ticket.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ICT Support Information */}
          <div className="bg-white p-4 sm:p-6 h-full rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-blue-600 font-poppins">
                ICT Support Information
              </h2>
              <div className="w-full sm:w-auto">
                {!ticket.ictAssignedTo && ticket.status !== 'RESOLVED' && (
                  <button
                    onClick={handleTakeActionClick}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-colors font-poppins text-sm shadow-sm disabled:opacity-50"
                  >
                    Take Action
                  </button>
                )}
                {ticket.ictAssignedTo && ticket.status === 'IN_PROGRESS' && (
                  <button
                    onClick={handleResolveClick}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:from-green-700 hover:to-green-600 transition-colors font-poppins text-sm shadow-sm disabled:opacity-50"
                  >
                    Resolve Ticket
                  </button>
                )}
                {ticket.status === 'RESOLVED' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-green-50 text-green-600 border-green-200">
                    Resolved
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Assigned To</div>
                <div className="flex items-center gap-2">
                  <PersonIcon className="w-5 h-5 text-blue-600" />
                  <span className={`${ticket.ictAssignedTo ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                    {ticket.ictAssignedTo || 'Not yet assigned'}
                  </span>
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Diagnosis Details</div>
                <div className={`${ticket.ictDiagnosisDetails ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                  {ticket.ictDiagnosisDetails || 'No diagnosis provided'}
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Fix Details</div>
                <div className={`${ticket.ictFixDetails ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                  {ticket.ictFixDetails || 'No fix details provided'}
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Date Fixed</div>
                <div className={`${ticket.ictDateFixed ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                  {ticket.ictDateFixed ? formatDate(ticket.ictDateFixed) : 'Not yet fixed'}
                </div>
              </div>
              <div className="py-2">
                <div className="text-sm font-medium text-gray-600 mb-2">Recommendations</div>
                <div className={`${ticket.ictRecommendations ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                  {ticket.ictRecommendations || 'No recommendations provided'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Details */}
        <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-blue-600 mb-4 sm:mb-6 font-poppins">
            Ticket Details
          </h2>
          <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
            {ticket.category === 'TROUBLESHOOTING' && (
              <TroubleshootingDetails 
                ticket={ticket} 
                formatDate={formatDate} 
                handleDownload={handleDownload}
              />
            )}

            {ticket.category === 'ACCOUNT_MANAGEMENT' && (
              <AccountManagementDetails ticket={ticket} />
            )}

            {ticket.category === 'DOCUMENT_UPLOAD' && (
              <DocumentUploadDetails ticket={ticket} handleDownload={handleDownload} />
            )}

            {ticket.category === 'TECHNICAL_ASSISTANCE' && (
              <TechnicalAssistanceDetails ticket={ticket} formatDate={formatDate} />
            )}
          </div>
        </div>

        {/* Updates History */}
        <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-blue-600 mb-4 sm:mb-6 font-poppins">
            Updates History
          </h2>
          <div className="space-y-4">
            {ticket.updates?.data.map((update) => (
              <div key={update.id} className="bg-blue-50 rounded-lg p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(update.newStatus)}`}>
                    {update.newStatus.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-gray-500 italic">
                    {formatDate(update.createdAt)}
                  </span>
                </div>
                <div className="bg-white p-4 rounded-lg border border-gray-200 text-gray-900">
                  {update.comment}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Update Dialog */}
      {statusDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 font-poppins text-blue-600 border-b border-gray-200 pb-2">
                Update Ticket Status
              </h3>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                >
                  <option value="PENDING">Pending</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                  Comment
                </label>
                <textarea
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment about this status update..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                />
              </div>
              <div className="flex space-x-3 border-t border-gray-200 pt-4">
                <button
                  onClick={() => setStatusDialogOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-poppins"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ICT Details Dialog */}
      {ictDetailsDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 font-poppins text-blue-600 border-b border-gray-200 pb-2">
                {newStatus === 'RESOLVED' ? 'Resolve Ticket' : 'Take Action on Ticket'}
              </h3>
              <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg font-poppins">
                {newStatus === 'RESOLVED'
                  ? 'Please provide resolution details to complete this ticket'
                  : 'Taking action on this ticket will automatically set its status to "In Progress"'
                }
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                    Assigned To *
                  </label>
                  <select
                    value={ictDetails.assignedTo}
                    onChange={(e) => setIctDetails({ ...ictDetails, assignedTo: e.target.value })}
                    disabled={Boolean(ticket.ictAssignedTo)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select ICT Staff</option>
                    <option value="Shaina Montano">Shaina Montano</option>
                    <option value="Mark Joseph Chaewon">Mark Joseph Chaewon</option>
                    <option value="Matthew Romero">Matthew Romero</option>
                  </select>
                </div>
                {newStatus === 'RESOLVED' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                        Diagnosis Details *
                      </label>
                      <textarea
                        rows={3}
                        value={ictDetails.diagnosisDetails}
                        onChange={(e) => setIctDetails({ ...ictDetails, diagnosisDetails: e.target.value })}
                        placeholder="Detailed diagnosis of the issue"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                          !ictDetails.diagnosisDetails ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                        Fix Details *
                      </label>
                      <textarea
                        rows={3}
                        value={ictDetails.fixDetails}
                        onChange={(e) => setIctDetails({ ...ictDetails, fixDetails: e.target.value })}
                        placeholder="Details of how the issue was fixed"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                          !ictDetails.fixDetails ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                        Date Fixed *
                      </label>
                      <input
                        type="datetime-local"
                        value={ictDetails.dateFixed}
                        onChange={(e) => setIctDetails({ ...ictDetails, dateFixed: e.target.value })}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins ${
                          !ictDetails.dateFixed ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      <p className="text-sm text-gray-500 mt-1 font-poppins">
                        {!ictDetails.dateFixed 
                          ? "Please select the date and time when the issue was fixed" 
                          : "Date and time when the issue was fixed"
                        }
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                    Status Update Comment *
                  </label>
                  <textarea
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment about the actions taken..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                  />
                </div>
              </div>
              <div className="flex space-x-3 border-t border-gray-200 pt-4 mt-6">
                <button
                  onClick={() => setIctDetailsDialogOpen(false)}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-poppins"
                >
                  Cancel
                </button>
                <button
                  onClick={handleIctActionClick}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-poppins disabled:opacity-50 ${
                    newStatus === 'RESOLVED' 
                      ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600'
                      : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    newStatus === 'RESOLVED' ? 'Complete Resolution' : 'Assign Ticket'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Snackbar */}
      {showNotification && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <div className={`px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-md font-poppins ${
            currentNotification?.type === 'success' ? 'bg-green-500 text-white' :
            currentNotification?.type === 'error' ? 'bg-red-500 text-white' :
            currentNotification?.type === 'warning' ? 'bg-yellow-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <span className="font-medium">{currentNotification?.message}</span>
              <button
                onClick={handleNotificationClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails; 